import {assignPolicyNumber, changeUserPolicyNumber, validatePolicyNumber} from 'ApplicationEntry/ducks/caseDetails';
import {ApplicationType} from 'ApplicationEntry/models/ApplicationType';
import {CaseDetails, matchCaseDetailsByType} from 'ApplicationEntry/models/CaseDetails';
import {isLoaded as isLoadedCN} from 'ApplicationEntry/models/CaseNumber';
import {matchPolicyNumber, PolicyNumber} from 'ApplicationEntry/models/PolicyNumber';
import React, {useRef, useState} from 'react';
import {useDispatch} from 'react-redux';
import Alert from 'Shared/components/Alert';
import {
  Button,
  CircularProgress,
  TextField,
  Grid,
  RadioGroup,
  FormControl,
  FormControlLabel,
  Radio,
} from '@material-ui/core';
import {Product} from 'ApplicationEntry/models/ProductsRiders/PlanModel';
import application from 'ApplicationEntry/ducks/application';

type PolicyNumberInputProps = {
  policyNumber: PolicyNumber;
  selectedPolicyTypes: Product[];
  applicationType: ApplicationType;
  caseDetails: CaseDetails;
};

const PolicyNumberInput = (props: PolicyNumberInputProps) => {
  const {policyNumber, selectedPolicyTypes} = props;
  const dispatch = useDispatch();
  const textInput = useRef<HTMLLabelElement | null>(null);
  const [isElectronic, setIsElectronic] = useState(false);

  const onPolicyNumberChange = (pn: string) => {
    if (pn?.length === 0) {
      setIsElectronic(false);
    } else {
      setIsElectronic(pn?.toLocaleLowerCase().startsWith('p') || pn.toLocaleLowerCase().startsWith('m') ? false : true);
    }
    dispatch(changeUserPolicyNumber(pn));
  };

  const isCaseNumberEmpty = () => {
    return matchCaseDetailsByType({
      Group: ({caseNumber}) => (isLoadedCN(caseNumber) ? false : true),
      NewYork: () => false,
      Ordinary: () => false,
    })(props.caseDetails);
  };

  const disableRadio = () => {
    return matchPolicyNumber({
      Unassigned: () => false,
      Loading: () => false,
      Loaded: pn => (pn.length > 0 ? true : false),
      Error: () => false,
      UserAssignedPolicyNumber: pn => (pn.length > 0 ? true : false),
      PolicyNumberValidating: () => false,
      Reserved: pn => (pn.length > 0 ? true : false),
      PolicyNumberError: () => false,
    })(policyNumber);
  };

  const disableNextBtn = () => {
    if (!isCaseNumberEmpty()) {
      return matchPolicyNumber({
        Unassigned: () => true,
        Loading: () => true,
        Loaded: () => (selectedPolicyTypes.length > 0 ? false : true),
        Error: () => false,
        UserAssignedPolicyNumber: pn => (pn.length > 0 || selectedPolicyTypes.length > 0 ? false : true),
        PolicyNumberValidating: () => false,
        Reserved: () => (selectedPolicyTypes.length > 0 ? false : true),
        PolicyNumberError: () => false,
      })(policyNumber);
    } else {
      return true;
    }
  };

  return (
    <>
      <Grid container spacing={4}>
        <Grid item sm={12} md={3}>
          {matchPolicyNumber({
            Loading: () => (
              <TextField
                label="Policy Number"
                variant="filled"
                placeholder="Policy Number"
                className="case-details-section__policy-number-input"
                value={''}
                InputProps={{
                  endAdornment: <CircularProgress className="case-details-section__loading-policy-number-icon" />,
                }}
              />
            ),
            Loaded: pn => (
              <TextField
                label="Policy Number"
                variant="outlined"
                placeholder="Policy Number"
                className="case-details-section__policy-number-input"
                value={pn}
                onChange={e => {
                  textInput.current?.focus();
                  onPolicyNumberChange(e.target.value as string);
                }}
              />
            ),
            Unassigned: () => (
              <TextField
                label="Policy Number"
                variant="filled"
                placeholder="Policy Number"
                className="case-details-section__policy-number-input"
                value={''}
                onChange={e => {
                  textInput.current?.focus();
                  onPolicyNumberChange(e.target.value as string);
                }}
                onClick={e => dispatch(changeUserPolicyNumber(''))}
                disabled={isCaseNumberEmpty()}
              />
            ),
            Error: () => (
              <Alert
                className="case-details-section__policy-number-error-label"
                message="An Error Ocurred While Assigning a Policy Number."
              />
            ),
            UserAssignedPolicyNumber: pn => (
              <TextField
                label="Policy Number"
                variant="outlined"
                placeholder="Enter Policy Number"
                className="case-details-section__policy-number-input"
                value={pn}
                onChange={e => onPolicyNumberChange(e.target.value as string)}
                id="user-assigned-policy-number-input-policy-number"
                disabled={isCaseNumberEmpty()}
                inputProps={{
                  maxLength: 7,
                }}
              />
            ),
            Reserved: pn => (
              <TextField
                label="Policy Number"
                variant="outlined"
                placeholder="Enter Policy Number"
                className="case-details-section__policy-number-input"
                value={pn}
                onChange={e => onPolicyNumberChange(e.target.value as string)}
              />
            ),
            PolicyNumberError: pn => (
              <TextField
                // id="outlined-error-helper-text"
                label="Policy Number"
                defaultValue={pn}
                helperText=""
                variant="outlined"
                onChange={e => onPolicyNumberChange(e.target.value as string)}
                className="case-details-section__policy-number-input"
                placeholder="Enter Policy Number"
              />
            ),
            PolicyNumberValidating: pn => (
              <TextField
                label="Policy Number"
                variant="filled"
                placeholder="Policy Number"
                className="case-details-section__policy-number-input"
                value={pn}
                InputProps={{
                  endAdornment: <CircularProgress className="case-details-section__loading-policy-number-icon" />,
                }}
                inputProps={{
                  maxLength: 7,
                }}
              />
            ),
          })(policyNumber)}
        </Grid>

        <Grid item sm={12} md={3}>
          <Button
            onClick={() => {
              if (policyNumber.key === 'UserAssignedPolicyNumber' && policyNumber.value.length > 0) {
                dispatch(validatePolicyNumber(policyNumber, selectedPolicyTypes));
              } else {
                dispatch(assignPolicyNumber(isElectronic));
              }
            }}
            variant="contained"
            color="secondary"
            className="case-details__assign-policy-number-button"
            // true unassigned
            disabled={disableNextBtn()}
          >
            {matchPolicyNumber({
              Unassigned: () => 'Assign Policy Number',
              Loading: () => 'Assigning Policy Number...',
              Loaded: () => 'Assign Policy Number',
              Error: () => 'Assign Policy Number',
              UserAssignedPolicyNumber: pn => (pn.length > 0 ? 'Check Policy Number' : 'Assign Policy Number'),
              PolicyNumberValidating: () => 'Checking Policy Number...',
              Reserved: () => 'Assign Policy Number',
              PolicyNumberError: () => 'Check Policy Number',
            })(policyNumber)}
          </Button>

          <FormControl component="fieldset">
            <RadioGroup
              name="policyNumberType"
              value={isElectronic.toString()}
              onChange={e => setIsElectronic(e.target.value === 'true')}
            >
              <FormControlLabel
                key="false"
                value="false"
                control={<Radio />}
                label="Assign Policy Numbers"
                disabled={disableRadio() || disableNextBtn()}
              />
              <FormControlLabel
                key="true"
                value="true"
                control={<Radio />}
                label="Assign Electronic Numbers"
                disabled={disableRadio() || disableNextBtn()}
              />
            </RadioGroup>
          </FormControl>
        </Grid>
      </Grid>
    </>
  );
};

export default PolicyNumberInput;
